import { useCallback, useEffect, useRef, useState } from 'react';

import { useCreateMobileCheckoutCartUrlQuery } from 'src/queries/payment/create-mobile-checkout-cart-url';

import { usePayByBankMessaging } from './use-pay-by-bank-messaging';

type UsePbbInstorePaymentProps = {
  enabled: boolean;
  onPaymentSuccess: () => void;
  shipmentId: number;
};

type UsePbbInstorePaymentResult = {
  instoreCartUrl: string | null;
  isLoading: boolean;
  isConfirming: boolean;
  error: Error | null;
};

export function usePbbInstorePayment({
  enabled,
  onPaymentSuccess,
  shipmentId,
}: UsePbbInstorePaymentProps): UsePbbInstorePaymentResult {
  const [isConfirming, setIsConfirming] = useState(false);
  const [sessionKey, setSessionKey] = useState(0);
  const hasSucceeded = useRef(false);

  // Each time the QR screen opens: fresh session key (forces a new network call
  // rather than serving TanStack Query's cached result), and reset transient state.
  // Each time it closes: clear isConfirming so re-opening always starts at the QR state.
  useEffect(() => {
    setIsConfirming(false);
    hasSucceeded.current = false;
    if (enabled) {
      setSessionKey((k) => k + 1);
    }
  }, [enabled]);

  const {
    data: cartUrlData,
    isLoading,
    error: cartUrlError,
  } = useCreateMobileCheckoutCartUrlQuery(enabled ? shipmentId : 0, sessionKey);

  const handlePaymentSuccess = useCallback(() => {
    if (!hasSucceeded.current) {
      hasSucceeded.current = true;
      onPaymentSuccess();
    }
  }, [onPaymentSuccess]);

  const handleCartOpened = useCallback(() => {
    setIsConfirming(true);
  }, []);

  usePayByBankMessaging({
    enabled,
    shipmentId,
    onCartOpened: handleCartOpened,
    onCartConfirmed: handlePaymentSuccess,
  });

  return {
    instoreCartUrl: cartUrlData?.instore_cart_url ?? null,
    isLoading,
    isConfirming,
    error: cartUrlError,
  };
}
